// PII scrubbing for crash-reporter events + breadcrumbs. One posture for
// every platform (mobile @sentry/react-native, web @sentry/react) so the
// zero-PII rule can't drift between two hand-rolled copies —
// [[feedback_zero_pii_logs]] + [[feedback_crash_visibility_before_recovery]].
//
// Typed against a minimal STRUCTURAL shape (only the fields the scrubbers
// touch), NOT against any @sentry/* type: packages/shared is a zod-only
// package also imported by apps/api (a Lambda with no Sentry footprint), so
// it must not couple to the SDK version matrix. Each caller passes its real
// Sentry.ErrorEvent / Sentry.Breadcrumb — TS structural typing does the rest.

// Keys that can carry user-typed text or identifiers. Matched
// case-insensitively at every object depth; the whole entry is dropped.
const PII_KEYS = new Set([
  "email",
  "username",
  "name",
  "ip",
  "ip_address",
  "cookies",
  "headers",
  "authorization",
  "auth",
  "token",
  "password",
  "phone",
  "address",
  "data",
  "body",
  "request_body",
]);

/** Deep-copy `obj`, dropping every key in the PII denylist at any depth. */
export function scrubObject(obj: unknown): unknown {
  if (obj === null || typeof obj !== "object") return obj;
  if (Array.isArray(obj)) return obj.map(scrubObject);
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
    if (PII_KEYS.has(k.toLowerCase())) continue;
    out[k] = scrubObject(v);
  }
  return out;
}

// The subset of a Sentry ErrorEvent the scrubber reads/mutates. A real
// ErrorEvent (from either SDK) is structurally assignable to this.
interface ScrubbableEvent {
  request?: { headers?: unknown; cookies?: unknown; data?: unknown };
  user?: {
    email?: unknown;
    username?: unknown;
    ip_address?: unknown;
    [key: string]: unknown;
  } | null;
  extra?: Record<string, unknown>;
  contexts?: Record<string, unknown>;
}

interface ScrubbableBreadcrumb {
  category?: string;
  data?: { [key: string]: unknown };
}

// Contexts we set ourselves (numeric/slug only) or that the SDK fills with
// non-PII metadata (versions, locale) — trusted, never scrubbed.
const TRUSTED_CONTEXTS = new Set(["activeWorkout", "app", "device", "os"]);

/**
 * `beforeSend` hook. Scope contexts are already applied to `event` here, so
 * this is the last chance to drop PII before transport: strip request
 * headers/cookies/body, user-typed identity fields, and deep-scrub `extra`
 * plus any non-trusted context.
 */
export function scrubEvent<E extends ScrubbableEvent>(event: E): E {
  if (event.request) {
    delete event.request.headers;
    delete event.request.cookies;
    delete event.request.data;
  }
  if (event.user) {
    // Keep an opaque id if the SDK already set one (Cognito sub); drop
    // anything user-typed. We don't actively populate `user`.
    delete event.user.email;
    delete event.user.username;
    delete event.user.ip_address;
  }
  if (event.extra) {
    event.extra = scrubObject(event.extra) as E["extra"];
  }
  if (event.contexts) {
    for (const [name, ctx] of Object.entries(event.contexts)) {
      if (TRUSTED_CONTEXTS.has(name)) continue;
      event.contexts[name] = scrubObject(ctx);
    }
  }
  return event;
}

/**
 * `beforeBreadcrumb` hook. Drops network breadcrumbs wholesale — their URL +
 * body can carry user-typed text (workout notes, plan names). The crash
 * stack + our own structured context are enough to repro without them.
 */
export function scrubBreadcrumb<B extends ScrubbableBreadcrumb>(
  breadcrumb: B,
): B | null {
  if (breadcrumb.category === "xhr" || breadcrumb.category === "fetch") {
    return null;
  }
  if (breadcrumb.data) {
    breadcrumb.data = scrubObject(breadcrumb.data) as B["data"];
  }
  return breadcrumb;
}
